<template>
    <main class="NewsPost">
        <div class="NewsPost__header-wrapper">
            <div class="NewsPost__header">
                <div class="NewsPost__header-content">
                    <CustomLink to="/blog">
                        <FontIcon
                            class="NewsPost__header-arrow"
                            icon="arrow"
                            size="small"
                            color="black"
                            :rotate="225"
                        />
                    </CustomLink>
                    <h1 v-if="newsPost.data.title" class="NewsPost__title">
                        {{ newsPost.data.title }}
                    </h1>
                    <time
                        class="NewsPost__publish-date"
                        :datetime="
                            newsPost.data.original_publish_date ||
                            newsPost.first_publication_date
                        "
                    >
                        {{
                            Intl.DateTimeFormat('en-GB', {
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric',
                            }).format(
                                new Date(
                                    newsPost.data.original_publish_date ??
                                        newsPost.first_publication_date,
                                ),
                            )
                        }}
                    </time>
                    <ul
                        v-if="calculatedNewsPostTags.length"
                        class="NewsPost__tags"
                    >
                        <li v-for="tag in calculatedNewsPostTags">
                            <CustomTag :text="tag.tag_text" :filled="true" />
                        </li>
                    </ul>
                </div>
                <SmartImage
                    class="NewsPost__header-image"
                    :url="newsPost.data.image.url"
                    :width="
                        newsPost.data.image.dimensions?.width
                            ? newsPost.data.image.dimensions.width
                            : 588
                    "
                    :height="
                        newsPost.data.image.dimensions?.height
                            ? newsPost.data.image.dimensions.height
                            : 331
                    "
                    :srcs="[{ width: 400 }, { width: 800 }, { width: 1200 }]"
                />
            </div>
        </div>
        <div class="NewsPost__body">
            <SliceZone
                v-if="newsPost?.data.slices"
                wrapper="div"
                :slices="newsPost?.data.slices ?? []"
                :components="components"
                class="NewsPost__slice-zone"
            />
            <div class="NewsPost__sharing-wrapper">
                <div class="NewsPost__sharing">
                    <div class="NewsPost__sharing">
                        <div class="NewsPost__share-title">Share</div>
                        <div class="NewsPost__share-links">
                            <CustomLink
                                class="NewsPost__share-link"
                                :href="`https://www.linkedin.com/sharing/share-offsite/?url=${
                                    $config.public.baseUrl + $route.fullPath
                                }`"
                            >
                                <FontIcon icon="linkedin" color="black" />
                            </CustomLink>
                            <CustomLink
                                class="NewsPost__share-link"
                                :href="`https://twitter.com/intent/tweet?url=${
                                    $config.public.baseUrl + $route.fullPath
                                }`"
                            >
                                <FontIcon icon="x" color="black" />
                            </CustomLink>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="NewsPost__related" v-if="relatedArticles.length">
            <h2 class="NewsPost__related-title">
                <template v-if="newsPost.data.related_articles_title">
                    {{ newsPost.data.related_articles_title }}
                </template>
                <template v-else>
                    Related <br />
                    articles
                </template>
            </h2>
            <ul class="NewsPost__related-list">
                <li
                    v-for="relatedArticle in relatedArticles"
                    class="NewsPost__related-list-item"
                >
                    <ImageCard :item="relatedArticle" />
                </li>
            </ul>
        </div>
        <div class="NewsPost__contact">
            <CustomLinkBanner
                :to="newsPost.data.banner_internal_link.url ?? '/contact'"
                :href="newsPost.data.banner_external_link.url"
                :line_1="newsPost.data.banner_text ?? `Let's talk`"
            />
        </div>
    </main>
</template>

<script setup>
import { components } from '~/slices'

const prismic = usePrismic()
const route = useRoute()

const { data: newsPost, error } = await useAsyncData(
    `[news-post-${route.params.post}]`,
    () => prismic.client.getByUID('new_post', route.params.post),
)

if (error.value) {
    throw createError({ status: 404 })
}

const { data: similarPosts } = await useAsyncData(
    `[similar-news-posts-${route.params.post}]`,
    () =>
        prismic.client.get({
            filters: [
                prismic.filter.at('document.type', 'new_post'),
                prismic.filter.similar(route.params.post, 2),
            ],
            fetchLinks: ['author.name'],
            pageSize: 2,
        }),
)

const { data: fallbackPosts } = await useAsyncData(
    `[fallback-news-posts-${route.params.post}]`,
    () =>
        prismic.client.get({
            filters: [
                prismic.filter.at('document.type', 'new_post'),
                prismic.filter.not('my.new_post.uid', route.params.post),
            ],
            fetchLinks: ['author.name'],
            pageSize: 2,
        }),
)

const relatedArticles = computed(() => {
    const builtArticles = []

    function buildForImageCard(item) {
        return {
            image: {
                path: item.data.image.url,
                dimensions: item.data.image?.dimensions,
            },
            title: item.data.title,
            listInfo: [
                {
                    text: Intl.DateTimeFormat('en-GB', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                    }).format(
                        new Date(
                            item.data.original_publish_date ??
                                item.first_publication_date,
                        ),
                    ),
                },
                { text: item.data.author.data?.name },
            ],
            tags: item.data.tags.map((tag) => tag.tag_text),
            to: item.url,
            cursorText: 'View<br>Post',
        }
    }

    if (similarPosts.value.results.length) {
        similarPosts.value.results.forEach((similarPost) => {
            builtArticles.push(buildForImageCard(similarPost))
        })
    }

    if (builtArticles.length < 2) {
        fallbackPosts.value.results.forEach((fallbackPost) => {
            if (builtArticles.length >= 2) return

            builtArticles.push(buildForImageCard(fallbackPost))
        })
    }

    return builtArticles
})

const calculatedNewsPostTags = computed(() => {
    return newsPost.value.data.tags.filter((tag) => tag.tag_text)
})

useSeoMeta({
    title:
        newsPost.value?.data?.meta_title ||
        newsPost.value?.data?.title ||
        'eight&four | A Performance Driven Social and Content Agency',
    description:
        newsPost.value?.data?.meta_description ||
        "We're a social & end-to-end content production agency. We work with global brands looking for a dynamic, multi-lingual agency.",
    ogTitle:
        newsPost.value?.data?.meta_title ||
        newsPost.value?.data?.title ||
        'eight&four | A Performance Driven Social and Content Agency',
    ogDescription:
        newsPost.value?.data?.meta_description ||
        "We're a social & end-to-end content production agency. We work with global brands looking for a dynamic, multi-lingual agency.",
    ogImage:
        newsPost.value?.data?.social_image?.url ||
        newsPost.value?.data?.image?.social?.url ||
        process.env.BASE_URL + '/images/default-og-image.png',
    articlePublishedTime:
        newsPost.value.data.original_publish_date ||
        newsPost.value.first_publication_date,
})

useHead({
    bodyAttrs: {
        class: 'theme--white hide-footer news-page',
    },
})

onMounted(() => {
    const { smoothScroller } = useGsap()

    smoothScroller?.effects('[data-speed]')
})
</script>

<style lang="scss">
.NewsPost {
    @include grid;

    color: var(--black);
}

.NewsPost__header-wrapper {
    @include grid;

    grid-column: 1 / span 24;
    border-bottom: 1px solid var(--black);
}

.NewsPost__header {
    display: flex;
    flex-direction: column-reverse;
    grid-column: 2 / span 22;
    row-gap: 2rem;
    margin-top: 8rem;
    margin-bottom: 5rem;

    @include large-up {
        @include grid($columns: 22);

        align-items: center;
        margin-top: 8.75rem;
        margin-bottom: 8.75rem;
    }
}

.NewsPost__title {
    @include heading-1;

    font-size: 1.75rem;
}

.NewsPost__publish-date {
    @include body-lg;

    display: block;
    margin-top: 1rem;
    margin-bottom: 1rem;

    @include medium-up {
        margin-top: 2rem;
        margin-bottom: 2rem;
    }
}

.NewsPost__header-arrow {
    margin-bottom: 2rem;

    @include large-up {
        margin-bottom: 5rem;
    }
}

.NewsPost__header-content {
    @include large-up {
        grid-column: span 12;
    }
}

.NewsPost__header-image {
    width: 100%;

    @include large-up {
        grid-column: span 9 / -1;
    }
}

.NewsPost__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
}

.NewsPost__body {
    display: flex;
    flex-direction: column;
    row-gap: 3rem;
    justify-content: space-between;
    margin-top: 5rem;
    margin-bottom: 5rem;
    grid-column: 2 / span 22;

    @media (min-width: 920px) {
        flex-direction: row;
        column-gap: 4rem;
    }
}

.NewsPost__slice-zone {
    max-width: clamp(600px, 50dvw, 900px);
}

.NewsPost__sharing-wrapper {
    position: relative;
}

.NewsPost__sharing {
    position: sticky;
    top: calc(var(--nav-offset) + 1.5rem);
}

.NewsPost__share-title {
    @include label-lg;

    margin-bottom: 0.75rem;
}

.NewsPost__share-links {
    display: flex;
    gap: 1.25rem;
}

.NewsPost__share-link {
    border: 1px solid var(--black);
    padding: 0.5rem;
}

.NewsPost__related {
    margin-bottom: 10rem;
}

.NewsPost__related,
.NewsPost__contact {
    grid-column: 2 / span 22;
}

.NewsPost__related-title {
    @include heading-2;

    margin-bottom: 2.5rem;
}

.NewsPost__related-list {
    @include grid($columns: 22);

    row-gap: 5rem;
}

.NewsPost__related-list-item {
    grid-column: span 22;

    &:nth-child(odd) {
        @include large-up {
            grid-column: 1 / span 10;
        }
    }

    &:nth-child(even) {
        @include large-up {
            grid-column: 13 / span 10;
            margin-top: 2.5rem;
        }
    }
}

.NewsPost__contact {
    margin-bottom: 5rem;
}
</style>
